<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { WorkerMissionStatus } from '@/types/mission';

// Types
import type { WorkersSelectionModal } from "@/modules/companyNeed/types/missions";

// Components
import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import Dropdown from "@/dsComponents/Dropdown/Dropdown.vue";

import WorkersPropositionsByStatus from "@/modules/companyNeed/components/modals/mission/propositions/WorkersPropositionsByStatus.vue";

import EmptyProposalsIllustration from "@/assets/illustrations/empty-mission.svg";

interface MissionPropositionsTabProps {
    workersSelection: WorkersSelectionModal[];
    positionQuantity: number;
    canAddWorkers?: boolean;
}

const props = withDefaults(defineProps<MissionPropositionsTabProps>(), {
    canAddWorkers: true,
});

const { t } = useI18n();

const emit = defineEmits<{
    (event: "openWorkerDetail", workerId: string): void;
    (event: "onWorkerStatusChange", workerId: string, newStatus: WorkerMissionStatus): void;
    (event: "openAddWorkers"): void;
}>();

const hasNoProposedWorkers = computed(() => props.workersSelection.length === 0 || props.workersSelection.every((ws) => ws.workers.length === 0));

const statusOrder = [
    WorkerMissionStatus.CONFIRMED,
    WorkerMissionStatus.ACCEPTED,
    WorkerMissionStatus.REPROPOSED,
    WorkerMissionStatus.REPROPOSED_BY_SMS,
    WorkerMissionStatus.PROPOSED,
    WorkerMissionStatus.ABORTED,
    WorkerMissionStatus.REJECTED_BY_AGENCY,
    WorkerMissionStatus.NOT_CONFIRMED,
    WorkerMissionStatus.DECLINED,
];

const statuses = computed(() => {
    const availableStatuses = props.workersSelection.map((ws) => ws.workerStatus);
    return [...new Set([...availableStatuses, WorkerMissionStatus.CONFIRMED])].sort((a, b) => {
        const indexA = statusOrder.indexOf(a);
        const indexB = statusOrder.indexOf(b);

        if (indexA === -1 && indexB === -1) return 0;
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;

        return indexA - indexB;
    });
});

const confirmedWorkerCount = computed(() => {
    const confirmedSection = props.workersSelection.find((ws) => ws.workerStatus === WorkerMissionStatus.CONFIRMED);
    return confirmedSection?.workers.length || 0;
});

const statusOpenMap = ref<Partial<Record<WorkerMissionStatus, boolean>>>({});

const initStatusOpen = () => {
    statusOpenMap.value = Object.fromEntries(statusOrder.map((key) => [key, checkIsOpen(key)]));
};

const handleNewWorkerStatusOpen = (excludeStatus: WorkerMissionStatus[]) => {
    statuses.value.forEach((key) => {
        if (!excludeStatus.includes(key)) {
            statusOpenMap.value[key] = checkIsOpen(key);
        }
    });
};

const checkIsOpen = (status: WorkerMissionStatus) => {
    if (status !== WorkerMissionStatus.CONFIRMED && confirmedWorkerCount.value >= props.positionQuantity) return false;
    if ([WorkerMissionStatus.ACCEPTED, WorkerMissionStatus.PROPOSED, WorkerMissionStatus.REPROPOSED, WorkerMissionStatus.CONFIRMED].includes(status))
        return true;
    return false;
};

const handleToggleShow = (status: WorkerMissionStatus) => {
    statusOpenMap.value[status] = !statusOpenMap.value[status];
};

const getWorkersForStatus = (status: WorkerMissionStatus) => {
    const section = props.workersSelection.find((ws) => ws.workerStatus === status);
    return section?.workers || [];
};

const handleOpenWorkerDetail = (workerId: string) => {
    emit("openWorkerDetail", workerId);
};

const handleWorkerStatusChange = (workerId: string, newStatus: WorkerMissionStatus) => {
    emit("onWorkerStatusChange", workerId, newStatus);
};

onBeforeMount(() => {
    initStatusOpen();
});

watch(
    () => statuses.value,
    (newVal, oldVal) => {
        const intersection = newVal.filter((value) => oldVal.includes(value));
        handleNewWorkerStatusOpen(intersection);
    }
);
</script>
<template>
    <div class="mission-propositions-tab">
        <template v-if="hasNoProposedWorkers">
            <div class="mission-propositions-tab__empty">
                <EmptyProposalsIllustration class="mission-propositions-tab__empty-illu" />
                <div class="mission-propositions-tab__empty__text">
                    <div class="typo-md-medium">
                        {{ t("modals.missionDetails.propositions.emptyTitle") }}
                    </div>
                    <div class="typo-xm-regular">
                        {{ t("modals.missionDetails.propositions.emptyDescription") }}
                    </div>
                </div>
                <Dropdown mode="hover" :disabled="canAddWorkers" width="fit-content" :styles="{ padding: 'var(--spacing-md, 8px)' }">
                    <template #trigger-zone>
                        <ButtonAlt
                            type="neutral"
                            variant="stroke"
                            size="sm"
                            left-icon="plus"
                            :disabled="!canAddWorkers"
                            :label="t('modals.missionDetails.propositions.addWorkerToMission.button')"
                            @click.stop="emit('openAddWorkers')"
                        />
                    </template>
                    <template #dropdown-content>
                        <span class="typo-xs-regular disabled-tooltip-text">
                            {{ t("modals.missionDetails.propositions.addWorkerToMission.disabledTooltip") }}
                        </span>
                    </template>
                </Dropdown>
            </div>
        </template>
        <template v-else>
            <div class="mission-propositions-tab__scroll">
                <WorkersPropositionsByStatus
                    v-for="status of statuses"
                    :key="status"
                    :status="status"
                    :workers="getWorkersForStatus(status)"
                    :positionQuantity="positionQuantity"
                    :isOpen="statusOpenMap[status] || false"
                    :isLast="status === statuses[statuses.length - 1]"
                    @toggleShow="handleToggleShow"
                    @openWorkerDetail="handleOpenWorkerDetail"
                    @onWorkerStatusChange="handleWorkerStatusChange"
                />
            </div>
        </template>
    </div>
</template>
<style scoped lang="scss">
.mission-propositions-tab {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    padding-right: var(--spacing-lg, 12px);
}

.mission-propositions-tab__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--spacing-4xl, 32px) 0;
    text-align: center;
    gap: var(--spacing-md, 8px);
    flex: 1;
    min-height: 0;
}

.mission-propositions-tab__empty-illu {
    max-width: 200px;
    height: auto;
}

.mission-propositions-tab__empty__text {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs, 4px);
    color: var(--text-sub-600, #4c505e);
}

.mission-propositions-tab__scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
}

.disabled-tooltip-text {
    color: var(--text-sub-600, #4c505e);
    white-space: nowrap;
}
</style>
